import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { FileText, ShieldCheck, Download, Loader2, X, Layers } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Prévisualisation lecture seule de l'attestation (pack1) et des CP/CG (pack2)
export default function SouscriptionDocumentsPreviewDialog({ isOpen, onClose, souscription, getAuthHeader }) {
  const [activeDoc, setActiveDoc] = useState('attestation');
  const [urls, setUrls] = useState({ attestation: null, cpcg: null, complet: null });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const isSigned = souscription?.signature_electronique || souscription?.paiement_valide
    || souscription?.statut === 'signee' || souscription?.statut === 'validee';
  const draftParam = isSigned ? 'false' : 'true';

  const fetchDoc = useCallback(async (endpoint) => {
    const resp = await axios.get(
      `${API}/souscriptions/${souscription.id}/${endpoint}?draft=${draftParam}`,
      { headers: getAuthHeader(), responseType: 'blob' }
    );
    return window.URL.createObjectURL(new Blob([resp.data], { type: 'application/pdf' }));
  }, [souscription, draftParam, getAuthHeader]);

  useEffect(() => {
    if (!isOpen || !souscription) return;
    let revoked = [];
    (async () => {
      setLoading(true); setError('');
      try {
        const [att, cp, full] = await Promise.all([
          fetchDoc('download-pack1'),
          fetchDoc('download-pack2'),
          fetchDoc('documents/rcds_unique'),
        ]);
        revoked = [att, cp, full];
        setUrls({ attestation: att, cpcg: cp, complet: full });
      } catch {
        setError("Impossible de charger les documents. Ils seront disponibles une fois générés.");
      } finally {
        setLoading(false);
      }
    })();
    return () => { revoked.forEach((u) => u && window.URL.revokeObjectURL(u)); };
  }, [isOpen, souscription, fetchDoc]);

  const handleDownload = (endpoint, label) => async () => {
    try {
      const resp = await axios.get(
        `${API}/souscriptions/${souscription.id}/${endpoint}?draft=${draftParam}`,
        { headers: getAuthHeader(), responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([resp.data]));
      const link = document.createElement('a');
      link.href = url; link.download = `${label}_${souscription.id}.pdf`;
      document.body.appendChild(link); link.click(); link.remove();
      window.URL.revokeObjectURL(url);
    } catch { toast.error('Erreur téléchargement'); }
  };

  const currentUrl = activeDoc === 'attestation'
    ? urls.attestation
    : activeDoc === 'cpcg' ? urls.cpcg : urls.complet;

  const downloadMap = {
    attestation: ['download-pack1', 'ATTESTATION'],
    cpcg: ['download-pack2', 'CP_CG'],
    complet: ['documents/rcds_unique', 'DOCUMENT_COMPLET'],
  };

  return (
    <Dialog open={isOpen} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-5xl h-[88vh] flex flex-col p-0" data-testid="souscription-preview-dialog">
        <DialogHeader className="px-6 pt-5 pb-3 border-b">
          <DialogTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5 text-blue-600" />
            Prévisualisation — {souscription?.raison_sociale || 'Souscription'}
            {!isSigned && (
              <span className="ml-2 text-xs font-normal text-amber-600 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5">
                Brouillon (lecture seule)
              </span>
            )}
          </DialogTitle>
        </DialogHeader>

        <div className="flex items-center justify-between gap-2 px-6 py-3 border-b bg-slate-50">
          <div className="flex gap-2">
            <Button
              variant={activeDoc === 'attestation' ? 'default' : 'outline'}
              size="sm" className="gap-1.5"
              onClick={() => setActiveDoc('attestation')}
              data-testid="preview-tab-attestation"
            >
              <ShieldCheck className="w-4 h-4" /> Attestation
            </Button>
            <Button
              variant={activeDoc === 'cpcg' ? 'default' : 'outline'}
              size="sm" className="gap-1.5"
              onClick={() => setActiveDoc('cpcg')}
              data-testid="preview-tab-cpcg"
            >
              <FileText className="w-4 h-4" /> CP / CG
            </Button>
            <Button
              variant={activeDoc === 'complet' ? 'default' : 'outline'}
              size="sm" className="gap-1.5"
              onClick={() => setActiveDoc('complet')}
              data-testid="preview-tab-complet"
            >
              <Layers className="w-4 h-4" /> Document complet
            </Button>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" className="gap-1.5"
              onClick={handleDownload(downloadMap[activeDoc][0], downloadMap[activeDoc][1])}
              data-testid="preview-download-btn"
            >
              <Download className="w-4 h-4" /> Télécharger
            </Button>
            <Button variant="ghost" size="sm" onClick={onClose} data-testid="preview-close-btn">
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>

        <div className="flex-1 overflow-hidden bg-slate-100">
          {loading && (
            <div className="h-full flex flex-col items-center justify-center text-slate-500 gap-3">
              <Loader2 className="w-8 h-8 animate-spin" />
              <p>Chargement des documents…</p>
            </div>
          )}
          {!loading && error && (
            <div className="h-full flex flex-col items-center justify-center text-slate-500 gap-2 px-8 text-center">
              <FileText className="w-10 h-10 text-slate-300" />
              <p>{error}</p>
            </div>
          )}
          {!loading && !error && currentUrl && (
            <iframe
              key={activeDoc}
              src={currentUrl}
              title={activeDoc}
              className="w-full h-full border-0"
              data-testid="preview-iframe"
            />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
